/*
 * körvonal "Húzz egy kártyát" — the digital sample.
 *
 * The honest version of free-plus-shipping for a card deck: the visitor draws
 * five REAL cards from the deck, one at a time, on the page — then the stack
 * turns into the buy card. No fake sample SKU, no shipping fee, no email gate
 * in front of the draw.
 *
 * One embeddable widget for every surface (PDP, gift-finder result, diary
 * advertorials, the huzz-egy-kartyat lander). Mount by attribute, the way
 * storefront/chat/advisor-widget.js does:
 *
 *   <div data-kv-sampler="egymas-szemeben" data-surface="pdp"></div>
 *
 *   data-surface   pdp | quiz | advertorial | lander   (event dimension)
 *   data-atc       "form" → on the PDP the buy card submits the theme's own
 *                  product form, so the cart drawer / oto-bundle interruptor
 *                  run exactly as they do for the real button. Default: the
 *                  /cart/<variant>:1 link.
 *   data-buy       "always" → the buy card is visible from the first paint
 *                  (a visitor who does not want five cards still finds the
 *                  product); default: revealed when the sample is used up.
 *   data-gate      "3" → after that many draws an UNKNOWN visitor (no email
 *                  from an earlier gate, not a logged-in customer) sees an
 *                  email card before the next draw; the address subscribes to
 *                  the HU/Master list (custom_source "Kártyahúzó") and the
 *                  draws continue. Off by default — the PDP A/B runs without
 *                  it; it is a lander arm to test (docs/card-sampler-plan.md §8).
 *   class kvs--compact / kvs--left   smaller card / left-aligned copy
 *
 * THE CARD IS THE PRINTED CARD. Geometry is the real 63x88 mm trim (a 63/88
 * aspect ratio, not a generic 5:7). The BACK is the print artwork itself
 * (`back.image`, cropped to trim from the print PDF); the FRONT is rebuilt in
 * CSS per deck — it has to be, because the question text is dynamic — from the
 * deck's printed layout: `circle` (körvonal, kids: pale disc behind left-set
 * sentence case), `bubble` (egymás szemében: orange speech bubble, white
 * centred caps), `corners` (Családban Marad: green quarter-discs on pink).
 *
 * QUESTIONS come from ONE file, storefront/sampler/questions.json, exposed as
 *   <script type="application/json" data-kv-sampler-pool data-src="…/questions.json"></script>
 * (empty body → fetched from data-src; non-empty body → parsed inline, which is
 * what the artifact build and the theme snippet do). A placeholder question
 * ("[KÉRDÉS n — operátor adja]") renders on a dashed MINTA face and is never
 * mistaken for copy; scripts/check-sampler-questions.mjs refuses to ship one.
 *
 * THE BUY CARD is page-supplied markup, not widget-generated: a sibling or
 * child `[data-kv-sampler-buy]` block in the kvhero/kvbtn shape that the
 * price-drift healer (lib/seo-lp-price-drift.ts fixCardBlocks) rewrites, so
 * the price and the "★ x.x · n értékelés" line self-heal like every other
 * lander card. The widget hides it on mount and reveals it when the sample is
 * used up. Without one (dev/mock only) it builds an equivalent from the pool.
 *
 * THE BUDGET IS PER PAGE LOAD: `drawsPerDeck` cards per deck (four), held in
 * memory only. A refresh starts over with a freshly shuffled order, so a
 * returning visitor never meets a dead widget — which is what persisting it
 * produced, and it read as broken rather than as a limit.
 *
 * That is a deliberately weak lock, and it is enough. The pool is 8-10 curated
 * questions per deck (`minPerDeck`), not the printed 80 — so however often
 * someone reloads, the web only ever shows those ten. The four-card limit is
 * there to make the sample feel like a sample inside one visit, not to defend
 * a deck nobody can reach anyway. `?sampler=reset` and `data-reset="show"`
 * remain as review conveniences.
 *
 * THE LAST CARD GOES BACK IN THE DECK. After the final draw the card flips
 * face-down again, so the block ends on the deck's own printed back — the stage
 * never leaves the layout, which is what keeps the footprint identical and the
 * page still. The caption row carries the closing line and the button row swaps
 * the draw button for add-to-cart. No status text is ever set on a card face:
 * a sentence in the question slot reads as a question.
 *
 * THE END STATE IS PER SURFACE (`data-end`):
 *   "buy"      the deck's product card — for pages with no buy box of their own
 *              (lander, advertorial, gift-finder). The default.
 *   "compact"  a closing line + one add-to-cart button, no product card — for
 *              the PDP, where price, reviews and image are already on the page
 *              and a second product card is just a duplicate of the buy box.
 *
 * Events (storefront-events endpoint, same token as the quiz/advisor):
 *   sampler_viewed (deck, surface)          once per visitor · deck · surface · day
 *   sampler_draw   (deck, surface, n, cardIndex, placeholder)
 *   sampler_exhausted (deck, surface)
 *   sampler_atc    (deck, surface, variantId, mode)
 *   sampler_gate_shown / sampler_gate_submitted (deck, surface, n)   gate mode only
 * LIVE gate: events post only on *.korvonal.com — a mock, a preview or the
 * artifact logs "[mock:sampler_*]" to the console and never reaches Otto.
 *
 * Reduced motion: crossfade instead of the 3D flip. Keyboard: the stack is a
 * <button> (Enter/Space draw), the question is an aria-live region, the
 * "Húzz még egyet" button and the buy card are ordinary focusable controls.
 *
 * SWIPE (2026-09-28, operator): the dots under the card read as a carousel, so
 * on phones people swipe the card and nothing happened — only a tap drew. A
 * horizontal drag now draws too: the card follows the finger, and past 50px it
 * flies off that side and the next question lands, exactly as a tap would.
 * Vertical movement is left to the page (touch-action: pan-y), so scrolling
 * over the card still scrolls. The click a drag leaves behind is swallowed, or
 * one swipe would draw twice. sampler_draw carries `via: tap | swipe`.
 */
(function () {
  "use strict";
  if (window.__kvSamplerLoaded) return;
  window.__kvSamplerLoaded = true;

  var LIVE = /(^|\.)korvonal\.com$/.test(location.hostname);
  var TK = "cmpismusd0000b965jxyb4r46";
  var EP = "https://api.korvonal.com/api/storefront-events/" + TK;
  var PLACEHOLDER_RE = /^\[KÉRDÉS \d+ — operátor adja\]$/;
  // Same list + revision as the quiz page and the advisor.
  var KL_COMPANY = "V4Rjis", KL_LIST = "WE8hLQ", KL_REV = "2026-07-15";
  var REDUCED = false;
  try { REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}

  function ls(k, v) { try { if (v === undefined) return localStorage.getItem(k); localStorage.setItem(k, v); return v; } catch (e) { return null; } }
  function uuid() { return (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : "v-" + Date.now() + "-" + Math.random().toString(16).slice(2); }
  var visitorId = ls("otto_visitor_id") || uuid(); ls("otto_visitor_id", visitorId);
  var seq = 0;

  function track(name, data, id) {
    var evId = id || ("smp:" + visitorId + ":" + Date.now() + ":" + (seq++));
    if (!LIVE) { try { console.log("[mock:" + name + "]", JSON.stringify(data || {})); } catch (e) {} return; }
    try {
      fetch(EP, { method: "POST", keepalive: true, headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: [{ id: evId, eventName: name, clientId: visitorId, visitorId: visitorId,
          occurredAt: new Date().toISOString(), url: location.href, data: data || {} }] }) }).catch(function () {});
    } catch (e) {}
  }
  function esc(s) { return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;"); }
  /* Hungarian needs the number spelled out and the article agreed with it, so
     changing drawsPerDeck rewrites the copy instead of stranding "öt" in it. */
  var NUM = {
    3: { word: "három", def: "a három", abl: "a háromból" },
    4: { word: "négy", def: "a négy", abl: "a négyből" },
    5: { word: "öt", def: "az öt", abl: "az ötből" },
    6: { word: "hat", def: "a hat", abl: "a hatból" },
    7: { word: "hét", def: "a hét", abl: "a hétből" }
  };
  function num(n) { return NUM[n] || { word: String(n), def: "a " + n, abl: "a " + n + "-ből" }; }
  /* The widget's own words per storefront language (data-lang on the mount,
     set by snippets/korvonal-sampler from request.locale). hu is the original
     copy, unchanged; cs/pl render only where the deck's sample_card
     metaobjects hold that edition's questions (2026-09-29). */
  var CS_PL_FORMS = { cs: ["karta", "karty", "karet"], pl: ["karta", "karty", "kart"] };
  function few(n) { return n >= 2 && n <= 4; }
  var UI = {
    hu: {
      draw: "Húzz egy kártyát", again: "Húzz még egyet", left: function (n) { return "(még " + n + ")"; },
      stage: function (n) { return "Húzz egy kártyát — még " + n + " van hátra"; }, empty: "A minta elfogyott",
      count: function (i, n) { return i + ". kártya " + num(n).abl; },
      done: function (n, rest) { return "Ez volt " + num(n).def + (rest ? " — a többi " + rest + " a dobozban van." : "."); },
      questions: function (t) { return t + " kérdés"; }, sample: "MINTA", cta: "Kosárba teszem", na: "Most nem kapható", restart: "↺ Újrakezdem"
    },
    cs: {
      draw: "Vytáhni si kartu", again: "Vytáhni další", left: function (n) { return "(zbývá " + n + ")"; },
      stage: function (n) { return "Vytáhni si kartu – zbývá " + n; }, empty: "Ukázka skončila",
      count: function (i, n) { return i + ". karta z " + n; },
      done: function (n, rest) { return (few(n) ? "To byly " : "To bylo ") + n + " " + (few(n) ? "karty" : "karet") + (rest ? " – dalších " + rest + " najdeš v krabičce." : "."); },
      questions: function (t) { return t + " otázek"; }, sample: "UKÁZKA", cta: "Vlož do košíku", na: "Momentálně není k dispozici", restart: "↺ Znovu"
    },
    pl: {
      draw: "Wyciągnij kartę", again: "Wyciągnij kolejną", left: function (n) { return "(zostało " + n + ")"; },
      stage: function (n) { return "Wyciągnij kartę – zostało " + n; }, empty: "Próbka się skończyła",
      count: function (i, n) { return i + ". karta z " + n; },
      done: function (n, rest) { return (few(n) ? "To były " : "To było ") + n + " " + (few(n) ? "karty" : "kart") + (rest ? (few(rest) ? " – pozostałe " + rest + " czekają w pudełku." : " – pozostałych " + rest + " czeka w pudełku.") : "."); },
      questions: function (t) { return t + " pytań"; }, sample: "PRZYKŁAD", cta: "Dodaj do koszyka", na: "Chwilowo niedostępne", restart: "↺ Od nowa"
    }
  };
  function upper1(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function ft(n) { return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " Ft"; }
  function isPlaceholder(q) { return PLACEHOLDER_RE.test(String(q).trim()); }

  // ── pool ─────────────────────────────────────────────────────────────────
  var poolPromise = null;
  function loadPool() {
    if (poolPromise) return poolPromise;
    poolPromise = new Promise(function (resolve) {
      if (window.KV_SAMPLER_POOL) return resolve(window.KV_SAMPLER_POOL);
      var el = document.querySelector('script[type="application/json"][data-kv-sampler-pool]');
      if (el && el.textContent.trim()) { try { return resolve(JSON.parse(el.textContent)); } catch (e) { return resolve(null); } }
      var src = el && el.getAttribute("data-src");
      if (!src) return resolve(null);
      fetch(src, { headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(resolve, function () { resolve(null); });
    });
    return poolPromise;
  }

  // ── per-visitor state ────────────────────────────────────────────────────
  function shuffle(n) {
    var a = []; for (var i = 0; i < n; i++) a.push(i);
    for (var j = a.length - 1; j > 0; j--) { var k = Math.floor(Math.random() * (j + 1)); var t = a[j]; a[j] = a[k]; a[k] = t; }
    return a;
  }
  /* Per page load, in memory. Shuffled fresh each time, so a reload shows a
     different four out of the ten rather than repeating the same set. */
  function stateFor(count) { return { order: shuffle(count), n: 0 }; }

  /* One-time cleanup: earlier builds persisted the budget here, and those keys
     would otherwise sit in visitors' browsers forever meaning nothing. */
  try {
    Object.keys(localStorage).forEach(function (k) { if (k.indexOf("kv_sampler:") === 0) localStorage.removeItem(k); });
  } catch (e) {}
  /* The budget lives for one page load, so a reload already restarts the flow;
     the review control below just does it in one click. */
  function reset() {}

  // ── live price + availability (same re-read as the quiz) ─────────────────
  function liveProduct(handle, done) {
    if (!LIVE) return done(null);
    fetch("/products/" + handle + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { done(j ? { price: Math.round((j.price || 0) / 100), available: !!j.available } : null); }, function () { done(null); });
  }

  // ── gate helpers ─────────────────────────────────────────────────────────
  function knownVisitor() {
    if (ls("kv_sampler_email")) return true;
    try { if (window.__st && window.__st.cid) return true; } catch (e) {}
    return false;
  }
  function subscribe(email, handle, surface) {
    ls("kv_sampler_email", email);
    if (!LIVE) { try { console.log("[mock:klaviyo subscribe]", email, handle); } catch (e) {} return; }
    try {
      fetch("https://a.klaviyo.com/client/subscriptions?company_id=" + KL_COMPANY, {
        method: "POST", keepalive: true,
        headers: { "Content-Type": "application/vnd.api+json", revision: KL_REV },
        body: JSON.stringify({ data: { type: "subscription", attributes: {
          custom_source: "Kártyahúzó",
          profile: { data: { type: "profile", attributes: { email: email,
            properties: { sampler_deck: handle, sampler_surface: surface },
            subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } } } } }
        }, relationships: { list: { data: { type: "list", id: KL_LIST } } } } })
      }).catch(function () {});
    } catch (e) {}
  }

  // ── markup ───────────────────────────────────────────────────────────────
  function backStyle(d) {
    var b = d.back || {};
    return "--kvs-bg:" + (b.bg || "#F3EDE2") + ";--kvs-bink:" + (b.ink || "#212F2F") + ";--kvs-label:" + (b.label || "#EF9DBC")
      + ";--kvs-linkink:" + (b.labelInk || "#fff") + ";--kvs-accent:" + (b.accent || "#F5D45E")
      + (b.image ? ";background-image:url('" + esc(b.image) + "')" : "");
  }
  function backClass(d) { var b = d.back || {}; return "kvs-back kvs-back--" + (b.image ? "image" : (b.pattern || "blobs")); }
  /** The printed front, as CSS custom properties + a style class. */
  function frontStyle(d) {
    var f = d.front || {};
    return "--kvs-fbg:" + (f.bg || "#FFFDF8") + ";--kvs-fink:" + (f.ink || "#212F2F") + ";--kvs-fshape:" + (f.shape || "#D8E7D7");
  }
  function frontClass(d) {
    var f = d.front || {};
    return "kvs-face kvs-face--" + (f.style || "circle")
      + (f.align === "center" ? " kvs-face--center" : "")
      + (f.case === "upper" ? " kvs-face--upper" : "")
      + (f.size === "word" ? " kvs-face--word" : "");
  }
  function fallbackBuy(handle, d) {
    var el = document.createElement("div");
    el.className = "kvhero kvs-buy";
    el.setAttribute("data-kv-sampler-buy", "");
    el.innerHTML = '<div class="kvs-buyin"><img src="' + esc(d.image || "") + '" alt="' + esc(d.name) + '" loading="lazy"><div class="kvbody">'
      + '<p class="kvname"><a href="/products/' + esc(handle) + '">' + esc(d.name) + "</a></p>"
      + '<p class="kvmeta">' + ft(d.priceHuf) + " · " + esc(d.subtitle || "") + "</p>"
      + (d.rating && d.reviewCount >= 10 ? '<p class="kvstars">★ ' + Number(d.rating).toFixed(1) + " · " + d.reviewCount + " értékelés</p>" : "")
      + '<a class="kvbtn" data-kv-sampler-atc href="/cart/' + esc(d.variantId) + ':1">Kosárba tesz</a></div></div>';
    return el;
  }

  function mount(root, pool) {
    var handle = root.getAttribute("data-kv-sampler");
    var surface = root.getAttribute("data-surface") || "page";
    var d = pool && pool.decks && pool.decks[handle];
    if (!d || !d.questions || !d.questions.length) { root.hidden = true; return; } // no questions → no sampler, never invented ones
    // On the live storefront a placeholder must never reach a customer: drop
    // them, and if too few real cards remain the sampler simply does not exist.
    // Off-domain (mocks, previews) they stay visible on the dashed MINTA face —
    // that is how the operator sees which decks are still waiting.
    if (LIVE) {
      var real = d.questions.filter(function (q) { return !isPlaceholder(q); });
      if (real.length < (Number(pool.drawsPerDeck) || 4)) { root.hidden = true; return; }
      d = Object.keys(d).reduce(function (o, k) { o[k] = d[k]; return o; }, {});
      d.questions = real;
    }
    var DRAWS = Number(pool.drawsPerDeck) || 4;
    var END = root.getAttribute("data-end") === "compact" ? "compact" : "buy";
    var SHOW_RESET = root.getAttribute("data-reset") === "show";
    var LANG = (root.getAttribute("data-lang") || "hu").slice(0, 2), T = UI[LANG] || UI.hu;
    // The gate subscribes to the HU/Master list: Hungarian pages only.
    var GATE = LANG === "hu" ? (Number(root.getAttribute("data-gate")) || 0) : 0;
    var BUY_ALWAYS = root.getAttribute("data-buy") === "always";
    var qs = d.questions;
    var st = stateFor(qs.length);

    // page-supplied buy card (healer-shaped) or the dev fallback
    var buy = root.querySelector("[data-kv-sampler-buy]");
    if (!buy && root.id) buy = document.querySelector('[data-kv-sampler-buy][data-for="' + root.id + '"]');
    if (!buy) buy = fallbackBuy(handle, d);
    buy.hidden = true;
    buy.classList.add("kvs-buy");

    root.classList.add("kvs");
    if (d.landscape) root.classList.add("kvs--landscape"); // printed 88x63 (körvonal 2, asszociáció)
    root.innerHTML =
      '<button type="button" class="kvs-stage" aria-label="' + esc(T.draw) + '">'
      + '<span class="' + backClass(d) + ' kvs-under kvs-under--3" style="' + backStyle(d) + '" aria-hidden="true"></span>'
      + '<span class="' + backClass(d) + ' kvs-under kvs-under--2" style="' + backStyle(d) + '" aria-hidden="true"></span>'
      + '<span class="kvs-card"><span class="' + backClass(d) + '" style="' + backStyle(d) + '">'
      // A real card back carries its own wordmark, so nothing is overlaid on it.
      // The affordance is the button under the stack, not text on the artwork.
      + ((d.back && d.back.image) ? "" :
          '<span class="kvs-back-label">' + esc(d.name) + "</span>"
          + '<span class="kvs-back-sub">' + (d.total ? T.questions(d.total) : "") + "</span>")
      + "</span>"
      + '<span class="' + frontClass(d) + '" style="' + frontStyle(d) + '">'
      + '<span class="kvs-shape" aria-hidden="true"></span>'
      + '<span class="kvs-tag" hidden>' + T.sample + '</span>'
      + '<p class="kvs-q" aria-live="polite"></p></span></span>'
      + "</button>"
      + '<div class="kvs-dots" aria-hidden="true"></div>'
      + '<p class="kvs-count"></p>'
      + '<button type="button" class="kvs-again"><span class="kvs-again-l">' + esc(T.draw) + '</span> <span class="kvs-again-n"></span></button>'
      + '<form class="kvs-gate" hidden novalidate>'
      + '<p class="kvs-gate-t">Add meg az e-mail címed, és jöhet a többi</p>'
      + '<p class="kvs-gate-s">Csak a további kártyákhoz kérjük. Le tudsz iratkozni bármikor.</p>'
      + '<label class="kvs-gate-lbl" for="kvs-mail-' + handle + '">E-mail</label>'
      + '<input class="kvs-gate-in" id="kvs-mail-' + handle + '" type="email" autocomplete="email" inputmode="email" placeholder="te@pelda.hu">'
      + '<p class="kvs-gate-err" role="alert" hidden>Ellenőrizd az e-mail címet — így nem tudjuk elküldeni.</p>'
      + '<button type="submit" class="kvs-gate-go">Kérem a többit</button>'
      + "</form>";
    root.appendChild(buy);
    if (BUY_ALWAYS) buy.hidden = false;

    var stage = root.querySelector(".kvs-stage"), card = root.querySelector(".kvs-card"), face = root.querySelector("[class*='kvs-face']");
    var qEl = root.querySelector(".kvs-q"), tag = root.querySelector(".kvs-tag");
    var dots = root.querySelector(".kvs-dots"), count = root.querySelector(".kvs-count"), again = root.querySelector(".kvs-again");
    var gate = root.querySelector(".kvs-gate"), gatePassed = false;
    /* One draw at a time. The stage was disabled during the animation but the
       "Húzz még egyet" button was not, so a fast double-click advanced twice
       and could schedule the closing state more than once. */
    var busy = false;

    function paintMeta() {
      var left = DRAWS - st.n, html = "";
      for (var i = 0; i < DRAWS; i++) html += '<span class="kvs-dot' + (i < st.n ? " on" : "") + '"></span>';
      dots.innerHTML = html;
      // Empty before the first draw (operator: drop the opening caption). The
      // row keeps its height in CSS so nothing shifts when it fills.
      count.textContent = st.n === 0 ? "" : T.count(st.n, DRAWS);
      again.hidden = left <= 0;
      again.querySelector(".kvs-again-l").textContent = st.n === 0 ? T.draw : T.again;
      again.querySelector(".kvs-again-n").textContent = st.n === 0 ? "" : T.left(left);
      stage.setAttribute("aria-label", left > 0 ? T.stage(left) : T.empty);
    }

    function showQuestion(idx) {
      var q = qs[idx], ph = isPlaceholder(q);
      qEl.textContent = q;
      face.classList.toggle("is-placeholder", ph);
      tag.hidden = !ph;
      return ph;
    }

    function finish() {
      root.setAttribute("data-stage", "done");
      // The stage STAYS. Hiding it collapses ~390px and jumps the page; the
      // card simply turns face-down, which is the whole point of the close.

      // Put the card back in the deck: flip it face-down onto the printed back.
      // The stage stays in the layout, so the footprint is unchanged by
      // construction rather than by a matching-size panel.
      card.classList.remove("is-open");
      stage.disabled = true;
      stage.setAttribute("aria-label", T.empty);

      // The closing line lives in the caption row — a status sentence, never on
      // a card face.
      count.textContent = T.done(DRAWS, d.total ? d.total - DRAWS : 0);
      count.setAttribute("role", "status");
      for (var i = 0; i < dots.children.length; i++) dots.children[i].className = "kvs-dot on";

      var done = document.createElement("div");
      done.className = "kvs-done" + (END === "compact" ? " kvs-done--compact" : "");
      root.insertBefore(done, buy);

      if (END === "compact") {
        // The PDP already shows price, rating and image above, and a sticky
        // add-to-cart below — so the end state is one button pointing at the
        // page's own form rather than a second product card.
        var cta = document.createElement("button");
        cta.type = "button";
        cta.className = "kvs-endcta";
        cta.setAttribute("data-kv-sampler-atc", "");
        // The label lives in a span because Combine's `.product-text
        // button:not(.button)` forces `color: inherit` on any button inside the
        // buy column, at a specificity our class cannot beat — which rendered
        // the label in the background colour, i.e. invisible. Nothing in the
        // theme targets this span, so colouring it wins without !important and
        // without borrowing the theme's own .button class.
        cta.innerHTML = '<span class="kvs-endcta-l"></span>';
        cta.firstChild.textContent = T.cta;
        done.appendChild(cta);
        wireAtc(cta);
        buy.hidden = true;
      } else {
        done.appendChild(buy);
        buy.hidden = false;
      }
      again.hidden = true;

      if (SHOW_RESET) {
        var again2 = document.createElement("button");
        again2.type = "button";
        again2.className = "kvs-restart";
        again2.textContent = T.restart;
        again2.addEventListener("click", function () { location.reload(); });
        done.appendChild(again2);
      }

      // Focus the caption rather than the CTA: the draw button just disappeared,
      // so focus would otherwise fall to the body — but focusing the button drew
      // a ring around a solid pill and made it look outlined.
      count.setAttribute("tabindex", "-1");
      try { count.focus({ preventScroll: true }); } catch (e) { count.focus(); }
    }

    // One add-to-cart path, whether it is the buy card's button or the PDP's
    // compact end CTA.
    function wireAtc(atc) {
      if (!atc) return;
      liveProduct(handle, function (p) {
        if (!p) return;
        var meta = buy.querySelector(".kvmeta");
        if (p.price && meta) meta.textContent = meta.textContent.replace(/^\S+\s\S+\sFt|^\S+\sFt/, ft(p.price));
        if (!p.available) {
          atc.setAttribute("aria-disabled", "true");
          var lbl = atc.querySelector(".kvs-endcta-l") || atc;
          lbl.textContent = T.na;
        }
      });
      atc.addEventListener("click", function (ev) {
        var mode = root.getAttribute("data-atc") === "form" ? "form" : "link";
        track("sampler_atc", { deck: handle, surface: surface, variantId: d.variantId, mode: mode, drawn: st.n });
        if (mode !== "form") return; // the /cart/<variant>:1 link does the rest
        var form = document.querySelector('form.shopify-product-form, form[action*="/cart/add"]');
        var submit = form && (form.querySelector('[type="submit"], button[name="add"]'));
        if (!submit) return;
        ev.preventDefault();
        if (!LIVE) { try { console.log("[mock:sampler_atc → theme form submit]"); } catch (e) {} return; }
        submit.click();
      });
    }
    if (END !== "compact") wireAtc(buy.querySelector("[data-kv-sampler-atc]"));

    // Email gate (data-gate="N"): shown instead of draw N+1 to unknown visitors.
    function showGate() {
      stage.hidden = true; again.hidden = true;
      gate.hidden = false;
      root.setAttribute("data-stage", "gate");
      track("sampler_gate_shown", { deck: handle, surface: surface, n: st.n });
      var input = gate.querySelector(".kvs-gate-in");
      setTimeout(function () { input.focus(); }, 50);
    }
    gate.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var input = gate.querySelector(".kvs-gate-in"), err = gate.querySelector(".kvs-gate-err");
      var v = (input.value || "").trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { err.hidden = false; input.focus(); return; }
      err.hidden = true;
      subscribe(v, handle, surface);
      track("sampler_gate_submitted", { deck: handle, surface: surface, n: st.n });
      gatePassed = true;
      gate.hidden = true; stage.hidden = false;
      root.setAttribute("data-stage", "stack");
      draw();
    });

    function draw() {
      if (busy || st.n >= DRAWS) return;
      busy = true;
      if (GATE && st.n >= GATE && !gatePassed && !knownVisitor()) { showGate(); return; }
      var idx = st.order[st.n];
      st.n += 1;
      stage.disabled = true;
      var open = card.classList.contains("is-open");
      var reveal = function () {
        busy = false;
        var ph = showQuestion(idx);
        track("sampler_draw", { deck: handle, surface: surface, n: st.n, cardIndex: idx, placeholder: ph, via: via });
        via = "tap";
        paintMeta();
        stage.disabled = false;
        if (st.n >= DRAWS) {
          track("sampler_exhausted", { deck: handle, surface: surface });
          setTimeout(finish, REDUCED ? 600 : 1400);
        }
      };
      if (REDUCED) {
        card.classList.add("is-snap", "is-open");
        card.classList.add("is-fading");
        setTimeout(function () { reveal(); card.classList.remove("is-fading"); }, 180);
        return;
      }
      if (open) {
        // Already face-up: the question leaves and the next one arrives. Going
        // back through the card back between two questions read as a mistake —
        // the visitor drew again, they did not put the card down.
        qEl.classList.add("is-out");
        setTimeout(function () {
          reveal();
          qEl.classList.remove("is-out");
          qEl.classList.add("is-in");
          setTimeout(function () { qEl.classList.remove("is-in"); }, 260);
        }, 190);
      } else {
        // First draw of the session: the stack really does flip over.
        card.classList.add("is-open");
        setTimeout(reveal, 210);
      }
    }

    root.setAttribute("data-stage", "stack");
    paintMeta();

    // Swipe to draw (see SWIPE in the header). Registered before the click
    // handler so the swallow runs first.
    var via = "tap", sx = 0, sy = 0, dx = 0, pid = null, tracking = false, dragging = false, swallow = false;
    stage.style.touchAction = "pan-y";
    function settle(anim) {
      stage.style.transition = anim ? "transform .2s ease" : "none";
      stage.style.transform = "";
      stage.style.opacity = "";
      setTimeout(function () { stage.style.transition = ""; }, anim ? 200 : 0);
    }
    stage.addEventListener("pointerdown", function (e) {
      if (busy || st.n >= DRAWS || (e.pointerType === "mouse" && e.button !== 0)) return;
      tracking = true; dragging = false; dx = 0; sx = e.clientX; sy = e.clientY; pid = e.pointerId;
    });
    stage.addEventListener("pointermove", function (e) {
      if (!tracking || e.pointerId !== pid) return;
      dx = e.clientX - sx;
      var dy = e.clientY - sy;
      if (!dragging) {
        if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          dragging = true;
          try { stage.setPointerCapture(pid); } catch (err) {}
        } else if (Math.abs(dy) > 10) { tracking = false; return; }
      }
      if (dragging) stage.style.transform = "translateX(" + dx + "px) rotate(" + (dx / 18) + "deg)";
    });
    function release() {
      if (!tracking) return;
      tracking = false;
      if (!dragging) return;
      swallow = true;
      setTimeout(function () { swallow = false; }, 350);
      if (Math.abs(dx) < 50) { settle(true); return; }
      via = "swipe";
      if (REDUCED) { settle(false); draw(); return; }
      var dir = dx > 0 ? 1 : -1;
      stage.style.transition = "transform .2s ease, opacity .2s ease";
      stage.style.transform = "translateX(" + (dir * 120) + "%) rotate(" + (dir * 12) + "deg)";
      stage.style.opacity = "0";
      setTimeout(function () { settle(false); draw(); }, 200);
    }
    stage.addEventListener("pointerup", release);
    stage.addEventListener("pointercancel", release);
    stage.addEventListener("click", function (e) {
      if (swallow) { e.preventDefault(); e.stopImmediatePropagation(); }
    });

    stage.addEventListener("click", draw);
    again.addEventListener("click", draw);

    // viewed: once per visitor · deck · surface · day, when half the stage is on screen
    var day = new Date().toISOString().slice(0, 10);
    var seenId = "smp_viewed:" + visitorId + ":" + handle + ":" + surface + ":" + day;
    var fired = false;
    function seen() { if (fired) return; fired = true; track("sampler_viewed", { deck: handle, surface: surface, drawn: st.n }, seenId); }
    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) { seen(); io.disconnect(); } }); }, { threshold: 0.5 });
      io.observe(root);
    } else seen();
  }

  function boot() {
    var roots = document.querySelectorAll("[data-kv-sampler]");
    if (!roots.length) return;
    loadPool().then(function (pool) {
      Array.prototype.forEach.call(roots, function (r) { if (!r.classList.contains("kvs")) mount(r, pool); });
    });
  }

  window.KorvonalSampler = {
    mount: function (el) { loadPool().then(function (pool) { if (el && !el.classList.contains("kvs")) mount(el, pool); }); },
    boot: boot
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
